import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  ColorPicker,
  Form,
  Input,
  Popconfirm,
  Select,
  Tag,
  Upload,
} from "antd";
import {
  CheckCircleFilled,
  ControlOutlined,
  EditOutlined,
  GlobalOutlined,
  IdcardOutlined,
  MailOutlined,
  PhoneOutlined,
  PoweroffOutlined,
  SaveOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { changeTimezone } from "../../actions/timezone";
import { loadUser, logout } from "../../actions/auth";
import apiClient from "../../actions/api";
import openNotification from "../../components/Notifications/openNotification";
import {
  COUNTRY_OPTIONS,
  normalizeJobTypeCountry,
} from "../../config/countries";
import { TIMEZONE_OPTIONS } from "../../config/timezones";
import ChangePassword from "./ChangePassword";
import LogoSrc from "../../assets/image/logo.svg";
import "../../style/user/accountSettings.scss";

const colorString = (color, fallback) => {
  if (typeof color === "string") return color || fallback;
  return color?.toHexString?.() || fallback;
};

const AccountSettings = ({ title }) => {
  document.title = title;
  const [form] = Form.useForm();
  const previewName = Form.useWatch("name", form);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const timezone = useSelector((state) => state.timezoneMode.timezoneMode);
  const [disabling, setDisabling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || "");
  const [backgroundColor, setBackgroundColor] = useState(
    user?.background_color || "#087b85",
  );
  const [textColor, setTextColor] = useState(user?.text_color || "#ffffff");

  const backgroundHex = useMemo(
    () => colorString(backgroundColor, "#087b85"),
    [backgroundColor],
  );
  const textHex = useMemo(() => colorString(textColor, "#ffffff"), [textColor]);

  useEffect(() => {
    form.setFieldsValue({
      name: user?.name,
      email: user?.email,
      phone: user?.phone,
      gender: user?.gender === undefined ? "" : String(user.gender),
      job_type: normalizeJobTypeCountry(user?.job_type),
    });
    setAvatarPreview(user?.avatar || "");
    setBackgroundColor(user?.background_color || "#087b85");
    setTextColor(user?.text_color || "#ffffff");
  }, [form, user]);

  useEffect(
    () => () => {
      if (avatarPreview?.startsWith("blob:"))
        URL.revokeObjectURL(avatarPreview);
    },
    [avatarPreview],
  );

  const updateTimezone = (value) => {
    localStorage.setItem("timezoneMode", value);
    dispatch(changeTimezone(value));
  };

  const beforeAvatarUpload = (file) => {
    const validType = ["image/jpeg", "image/jpg", "image/png"].includes(
      file.type,
    );
    if (!validType) {
      openNotification(
        "error",
        "Invalid image",
        "Only PNG, JPG and JPEG images are allowed.",
      );
      return Upload.LIST_IGNORE;
    }
    if (file.size > 2 * 1024 * 1024) {
      openNotification(
        "error",
        "Image too large",
        "Please choose an image smaller than 2 MB.",
      );
      return Upload.LIST_IGNORE;
    }
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    return false;
  };

  const saveSettings = async (values) => {
    setSaving(true);
    const data = new FormData();
    data.append("name", values.name.trim());
    data.append("phone", values.phone || "");
    data.append("gender", values.gender || "");
    data.append("job_type", values.job_type);
    data.append("background_color", backgroundHex);
    data.append("text_color", textHex);
    if (avatarFile) data.append("avatar", avatarFile);

    try {
      await apiClient.post("/users/account-settings", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await dispatch(loadUser());
      setAvatarFile(null);
      openNotification(
        "success",
        "Settings saved",
        "Your account was updated.",
      );
    } catch (error) {
      const errors = error?.response?.data?.errors;
      if (Array.isArray(errors)) {
        errors.forEach(({ msg }) =>
          openNotification("error", "Unable to save settings", msg),
        );
      } else {
        openNotification(
          "error",
          "Unable to save settings",
          "Please try again.",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const disableAccount = async () => {
    setDisabling(true);
    try {
      await apiClient.post("/users/disable-account");
      localStorage.removeItem("token");
      dispatch(logout());
      window.location.href = "/login";
    } catch (error) {
      openNotification(
        "error",
        "Account not disabled",
        error?.response?.data?.errors?.[0]?.msg ||
          "Unable to disable your account.",
      );
      setDisabling(false);
    }
  };

  return (
    <div className="account-settings-page">
      <header className="account-settings-heading">
        <span className="account-settings-heading-icon">
          <ControlOutlined />
        </span>
        <div>
          <h2>Account Settings</h2>
          <p>
            Manage your account details, appearance, regional preferences, and
            security.
          </p>
        </div>
      </header>

      <Form form={form} layout="vertical" onFinish={saveSettings}>
        <section
          className="account-overview-card"
          aria-labelledby="account-information-title"
        >
          <div className="account-identity">
            <Upload
              accept=".jpg,.jpeg,.png"
              beforeUpload={beforeAvatarUpload}
              maxCount={1}
              showUploadList={false}
            >
              <button
                className="account-avatar-editor"
                type="button"
                aria-label="Change profile image"
              >
                <img
                  src={avatarPreview || LogoSrc}
                  alt={`${user?.name || "User"} profile`}
                />
                <span className="account-avatar-overlay">
                  <EditOutlined /> Edit photo
                </span>
              </button>
            </Upload>
            <div>
              <span>Personal account</span>
              <h3>{user?.name || "Not provided"}</h3>
              <p>{user?.email || "Not provided"}</p>
              <Tag color={user?.email_verified ? "success" : "default"}>
                {user?.email_verified
                  ? "Verified account"
                  : "Verification pending"}
              </Tag>
            </div>
          </div>

          <div className="account-information">
            <div className="account-section-title">
              <div>
                <h3 id="account-information-title">User information</h3>
                <p>
                  Edit your details below. Email and verification status are
                  read-only.
                </p>
              </div>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                Save Changes
              </Button>
            </div>

            <div className="account-details-grid account-edit-grid">
              <Form.Item
                label={
                  <>
                    <UserOutlined /> Full name
                  </>
                }
                name="name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Please enter your name.",
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={
                  <>
                    <MailOutlined /> Email address
                  </>
                }
                name="email"
              >
                <Input disabled />
              </Form.Item>
              <Form.Item
                label={
                  <>
                    <PhoneOutlined /> Phone number
                  </>
                }
                name="phone"
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={
                  <>
                    <IdcardOutlined /> Gender
                  </>
                }
                name="gender"
              >
                <Select
                  options={[
                    { value: "", label: "Not specified" },
                    { value: "0", label: "Male" },
                    { value: "1", label: "Female" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Country"
                name="job_type"
                rules={[
                  { required: true, message: "Please choose a country." },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={COUNTRY_OPTIONS}
                />
              </Form.Item>
              <div className="account-verification-field">
                <label>
                  <CheckCircleFilled /> Email verified
                </label>
                <Checkbox checked={Boolean(user?.email_verified)} disabled>
                  {user?.email_verified ? "Verified" : "Not verified"}
                </Checkbox>
              </div>
              <div className="account-color-field">
                <label>Background color</label>
                <div>
                  <ColorPicker
                    value={backgroundColor}
                    onChange={setBackgroundColor}
                  />
                  <span>{backgroundHex}</span>
                </div>
              </div>
              <div className="account-color-field">
                <label>Text color</label>
                <div>
                  <ColorPicker value={textColor} onChange={setTextColor} />
                  <span>{textHex}</span>
                </div>
              </div>
              <div className="account-appearance-preview">
                <div className="account-preview-heading">
                  <span>Appearance preview</span>
                  <small>See how your service profile colors will look.</small>
                </div>
                <div
                  className="account-preview-swatch"
                  style={{ backgroundColor: backgroundHex, color: textHex }}
                >
                  <div className="account-preview-avatar">
                    {avatarFile ? (
                      <img src={avatarPreview} alt="Selected avatar preview" />
                    ) : (
                      <span>
                        {(previewName || user?.name || "U")
                          .trim()
                          .charAt(0)
                          .toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <strong>{previewName || user?.name || "Your name"}</strong>
                    <span>Resume Builder profile</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="account-timezone-section">
              <div className="account-timezone-copy">
                <span>
                  <GlobalOutlined />
                </span>
                <div>
                  <h4>Timezone</h4>
                  <p>Controls dates and times throughout Resume Builder.</p>
                </div>
              </div>
              <Select
                showSearch
                value={timezone}
                options={TIMEZONE_OPTIONS}
                onChange={updateTimezone}
                optionFilterProp="label"
                aria-label="Account timezone"
              />
            </div>

            <div className="account-danger-zone">
              <div className="account-danger-copy">
                <span>
                  <PoweroffOutlined />
                </span>
                <div>
                  <h4>Disable account</h4>
                  <p>
                    Pause access to your account. An administrator must
                    re-enable it before you can sign in again.
                  </p>
                </div>
              </div>
              <Popconfirm
                title="Disable your account?"
                description="You will be signed out immediately and cannot sign in until an administrator re-enables your account."
                okText="Disable account"
                cancelText="Keep account"
                okButtonProps={{ danger: true, loading: disabling }}
                onConfirm={disableAccount}
              >
                <Button
                  danger
                  type="primary"
                  icon={<PoweroffOutlined />}
                  loading={disabling}
                >
                  Disable Account
                </Button>
              </Popconfirm>
            </div>
          </div>
        </section>
      </Form>

      <section
        className="account-password-section"
        aria-label="Password security"
      >
        <ChangePassword title={title} />
      </section>
    </div>
  );
};

export default AccountSettings;
